// TimelineLens — the everyday "what happened" browse, searchable.
//
// The whole authoring arc as a scannable feed: date-section headers chunk the
// scroll, a search box (the shared DSL, incl. the `by:` actor axis) and filter
// chips (kind · actor) narrow it, and "show internal events" is OFF by default
// (the power-user escape hatch). Every narrowing is pure (`filterSections`);
// the only state here is ephemeral view-state (the query text, which chips are
// on) — nothing domain, nothing I/O. Rows arrive resolved + pre-sectioned; the
// actions (reveal / expand / KB door) are props that reach OUT.
//
// This is a read-only lens: it never rewrites the log. Its cold state is a calm
// call to action, never a blank void.

export { TimelineLens };

import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useMemo, useState } from "react";
import { EmptyState } from "../../EmptyState/EmptyState.js";
import { Icon } from "../../Icon/Icon.js";
import { Switch } from "../../Switch/Switch.js";
import { Text } from "../../Text/Text.js";
import type { TimelineActor, TimelineRow, TimelineSection } from "../types.js";
import {
    filterSections,
    TIMELINE_GROUPS,
    type TimelineGroup,
} from "./filter.js";
import { TimelineRow as Row } from "./TimelineRow.js";
import "./TimelineLens.css";

const ACTORS: readonly TimelineActor[] = ["you", "assistant", "app"];

type TimelineLensProps = {
    // The whole history, pre-folded, resolved, and bucketed into date sections
    // by the shell (newest first). Empty ⇒ the cold state.
    readonly sections: readonly TimelineSection[];
    readonly onReveal?: (row: TimelineRow) => void;
    readonly onExpandResource?: (row: TimelineRow) => void;
    readonly onOpenKnowledge?: (row: TimelineRow) => void;
};

const TimelineLens = ({
    sections,
    onReveal,
    onExpandResource,
    onOpenKnowledge,
}: TimelineLensProps): ReactNode => {
    const { t } = useLingui();
    const [queryText, setQueryText] = useState("");
    const [activeGroups, setActiveGroups] = useState<
        ReadonlySet<TimelineGroup>
    >(() => new Set(TIMELINE_GROUPS));
    const [activeActors, setActiveActors] = useState<
        ReadonlySet<TimelineActor>
    >(() => new Set(ACTORS));
    const [showInternal, setShowInternal] = useState(false);

    const filtered = useMemo(
        () =>
            filterSections(sections, {
                queryText,
                activeGroups,
                activeActors,
                showInternal,
            }),
        [sections, queryText, activeGroups, activeActors, showInternal],
    );

    const groupLabel: Record<TimelineGroup, string> = {
        source: t`Sources`,
        material: t`Materials`,
        export: t`Exports`,
        fact: t`Facts`,
        session: t`Sessions`,
        project: t`Projects`,
    };
    const actorLabel: Record<TimelineActor, string> = {
        you: t`You`,
        assistant: t`The assistant`,
        app: t`the app`,
    };

    const isColdStart = sections.length === 0;

    return (
        <div className="bilda-hi-timeline">
            <div className="bilda-hi-timeline__controls">
                <div className="bilda-hi-timeline__search">
                    <span
                        className="bilda-hi-timeline__search-icon"
                        aria-hidden="true"
                    >
                        <Icon name="search" size="sm" />
                    </span>
                    <input
                        type="search"
                        className="bilda-hi-timeline__search-input"
                        value={queryText}
                        onChange={(e) => setQueryText(e.target.value)}
                        placeholder={t`Search your history`}
                        aria-label={t`Search your history`}
                    />
                </div>
                {/* biome-ignore lint/a11y/useSemanticElements: a named non-landmark toggle-chip group is only expressible as div[role=group] — <fieldset> is a form idiom and <section> is a page landmark (the Palette precedent). */}
                <div
                    className="bilda-hi-timeline__chips"
                    role="group"
                    aria-label={t`Filter by kind`}
                >
                    {TIMELINE_GROUPS.map((group) => (
                        <Chip
                            key={group}
                            label={groupLabel[group]}
                            active={activeGroups.has(group)}
                            onToggle={() =>
                                setActiveGroups(toggle(activeGroups, group))
                            }
                        />
                    ))}
                </div>
                {/* biome-ignore lint/a11y/useSemanticElements: a named non-landmark toggle-chip group is only expressible as div[role=group] — <fieldset> is a form idiom and <section> is a page landmark (the Palette precedent). */}
                <div
                    className="bilda-hi-timeline__chips"
                    role="group"
                    aria-label={t`Filter by who`}
                >
                    {ACTORS.map((actor) => (
                        <Chip
                            key={actor}
                            label={actorLabel[actor]}
                            active={activeActors.has(actor)}
                            onToggle={() =>
                                setActiveActors(toggle(activeActors, actor))
                            }
                        />
                    ))}
                </div>
                <div className="bilda-hi-timeline__internal">
                    <span aria-hidden="true">
                        <Text as="span" size="xs" tone="dim">
                            <Trans>Show internal events</Trans>
                        </Text>
                    </span>
                    <Switch
                        checked={showInternal}
                        onCheckedChange={setShowInternal}
                        label={t`Show internal events`}
                    />
                </div>
            </div>

            {isColdStart ? (
                <EmptyState
                    icon={<Icon name="clock" size="lg" />}
                    title={<Trans>Your work will appear here as you go</Trans>}
                    body={
                        <Trans>
                            Every source you add, material you author, and
                            change you make is recorded here — nothing is
                            hidden.
                        </Trans>
                    }
                />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Icon name="search" size="lg" />}
                    title={<Trans>No moments match</Trans>}
                    body={
                        <Trans>
                            Try a broader search or turn a filter back on.
                        </Trans>
                    }
                />
            ) : (
                <div className="bilda-hi-timeline__feed">
                    {filtered.map((section) => (
                        <section
                            key={section.label}
                            className="bilda-hi-timeline__section"
                        >
                            <Text
                                as="p"
                                size="xs"
                                weight="semibold"
                                tone="dim"
                                className="bilda-hi-timeline__section-label"
                            >
                                {section.label}
                            </Text>
                            <ul className="bilda-hi-timeline__rows">
                                {section.rows.map((row) => (
                                    <Row
                                        key={row.id}
                                        row={row}
                                        onReveal={onReveal}
                                        onExpandResource={onExpandResource}
                                        onOpenKnowledge={onOpenKnowledge}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
};

// A toggle chip — a filter that is ON by default. `aria-pressed` carries the
// state to AT; the visual selected treatment is a lifted fill + weight (not
// colour alone). All-on is the default so the timeline shows everything.
const Chip = ({
    label,
    active,
    onToggle,
}: {
    label: string;
    active: boolean;
    onToggle: () => void;
}): ReactNode => (
    <button
        type="button"
        className="bilda-hi-chip"
        aria-pressed={active}
        onClick={onToggle}
    >
        {label}
    </button>
);

// Add-or-remove a value in a set, returning a new set (pure, no mutation).
const toggle = <A,>(set: ReadonlySet<A>, value: A): ReadonlySet<A> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
};
